import type {
  InputHTMLAttributes,
  ReactNode,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from "react";
import "./field.css";

type Wiring = "id" | "required" | "className" | "aria-invalid" | "aria-describedby" | "children";

type Common = {
  /** id of the control; the label points to it and the message is `${id}-error`. */
  id: string;
  label: ReactNode;
  /** Sets `required` on the control and draws a `*` after the label (hidden from assistive tech). */
  required?: boolean;
  /** Message shown under the control while `invalid`. Always rendered (hidden otherwise), so its text is in the HTML. */
  error?: string;
  /** Red border, `aria-invalid` on the control, and the message linked through `aria-describedby`. */
  invalid?: boolean;
  className?: string;
};

type InputFieldProps = Common & { control?: "input" } & Omit<InputHTMLAttributes<HTMLInputElement>, Wiring>;
type TextareaFieldProps = Common & { control: "textarea" } & Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, Wiring>;
type SelectFieldProps = Common & { control: "select"; children: ReactNode } & Omit<
    SelectHTMLAttributes<HTMLSelectElement>,
    Wiring
  >;

export type FieldProps = InputFieldProps | TextareaFieldProps | SelectFieldProps;

/** Stable id of a field's error message. */
export const fieldErrorId = (id: string) => `${id}-error`;

function Chevron() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 6l4 4 4-4" />
    </svg>
  );
}

/** Labelled form control (input, textarea or select) with its required mark and error message. */
export default function Field(props: FieldProps) {
  const { id, label, required = false, error, invalid = false, className, control: kind = "input", ...rest } = props;
  const errorId = fieldErrorId(id);
  const wiring = {
    id,
    required,
    className: "ds-field__control",
    "aria-invalid": invalid || undefined,
    // Only while the message is on screen: a hidden element would give an empty description.
    "aria-describedby": invalid && error ? errorId : undefined,
  };

  let control: ReactNode;
  if (kind === "select") {
    const { children, ...select } = rest as Omit<SelectFieldProps, keyof Common | "control">;
    control = (
      <span className="ds-field__select">
        <select {...select} {...wiring}>
          {children}
        </select>
        <span className="ds-field__chevron" aria-hidden="true">
          <Chevron />
        </span>
      </span>
    );
  } else if (kind === "textarea") {
    control = <textarea {...(rest as Omit<TextareaFieldProps, keyof Common | "control">)} {...wiring} />;
  } else {
    control = <input {...(rest as Omit<InputFieldProps, keyof Common | "control">)} {...wiring} />;
  }

  return (
    <div className={`ds-field${invalid ? " ds-field--invalid" : ""}${className ? ` ${className}` : ""}`}>
      <label className="ds-field__label" htmlFor={id}>
        {label}
        {required && (
          <span className="ds-field__required" aria-hidden="true">
            {" "}
            *
          </span>
        )}
      </label>
      {control}
      {error && (
        <p className="ds-field__error" id={errorId} hidden={!invalid}>
          {error}
        </p>
      )}
    </div>
  );
}
